import { useState, useCallback, useEffect } from "react";
import { Box, Typography, CircularProgress, Stack } from "@mui/material";
// components
import Card from "../../../components/Discover/card-view/Card";
import SimilarCreatorsTabSkeleton from "./SimilarCreatorsTabSkeleton";
// hooks
import { useInfiniteScroll } from "../../../hooks/useInfiniteScroll";
// config
import colors from "../../../config/colors";
// redux
import { useDispatch } from "../../../redux/store";
import { setSimilarCreatorDetails } from "../../../redux/slices/creator";
import { useLazyGetSimilarCreatorsQuery } from "../../../redux/apiSlices/discover";
// ga
import {
  triggerGa,
  CATEGORY,
  ACTION,
  PAGE,
  SCREEN,
  LABEL,
} from "../../../utils/gaUtils";

const SimilarCreatorsTab = ({ wishlink_username }) => {
  const dispatch = useDispatch();
  const [currentPage, setCurrentPage] = useState(1);
  const [creators, setCreators] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  const [fetchSimilarCreators, { isFetching }] =
    useLazyGetSimilarCreatorsQuery();

  const hasMore = pagination?.has_next || false;

  const loadSimilarCreatorsData = useCallback(
    async (page) => {
      if (!wishlink_username) return;

      try {
        const result = await fetchSimilarCreators({
          wishlink_username,
          pagination: {
            page,
            rows_per_page: 10,
          },
        }).unwrap();

        if (page === 1) {
          setCreators(result?.creators || []);
        } else {
          setCreators((prev) => [...prev, ...(result?.creators || [])]);
        }

        setPagination(result?.pagination);
        setIsInitialLoading(false);
      } catch (error) {
        console.error("Error loading similar creators:", error);
        setIsInitialLoading(false);
      }
    },
    [wishlink_username, fetchSimilarCreators],
  );

  const loadMore = useCallback(() => {
    if (hasMore && !isFetching) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      loadSimilarCreatorsData(nextPage);
    }
  }, [hasMore, isFetching, currentPage, loadSimilarCreatorsData]);

  const containerRef = useInfiniteScroll(loadMore, hasMore, isFetching, 100);

  useEffect(() => {
    if (wishlink_username) {
      setCurrentPage(1);
      setCreators([]);
      setPagination(null);
      loadSimilarCreatorsData(1);
    }
  }, [wishlink_username, loadSimilarCreatorsData]);

  const handleRowClick = (item) => {
    triggerGa({
      category: CATEGORY.CREATOR_DISCOVERY,
      action: ACTION.BUTTON_CLICK,
      label: LABEL.PROFILE_CLICK,
      label3: item?.wishlink_username,
      page: PAGE.PROFILE,
      screenName: SCREEN.SIMILAR_CREATORS,
    });
    dispatch(setSimilarCreatorDetails(item));
  };

  if (isInitialLoading) {
    return <SimilarCreatorsTabSkeleton />;
  }

  if (creators?.length === 0) {
    return (
      <Box
        sx={{
          height: "70vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography
          variant="body1"
          color={colors.gray200}
          sx={{ textAlign: "center" }}
        >
          No similar creators found for this profile.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      ref={containerRef}
      sx={{
        height: "100vh",
        overflow: "auto",
        pr: 0.5,
        "&::-webkit-scrollbar": {
          width: "4px",
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: colors.gray100,
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: colors.primary500,
          borderRadius: "2px",
        },
        "&::-webkit-scrollbar-thumb:hover": {
          backgroundColor: colors.primary500,
          opacity: 0.8,
        },
      }}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
          gap: 1,
        }}
      >
        {creators?.map((item, index) => (
          <Card
            key={`${item?.wishlink_username}-${index}`}
            item={item}
            onClick={() => handleRowClick(item)}
            type="similar-creators"
          />
        ))}
      </Box>
      {isFetching && !isInitialLoading && (
        <Stack alignItems="center" py={2}>
          <CircularProgress size={24} />
        </Stack>
      )}
    </Box>
  );
};

export default SimilarCreatorsTab;
